import { Input } from '@mui/joy';

import { ChevronDownIcon } from 'lucide-react';
import { useState } from 'react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/Dropdown';
import { lowerCaseIncludes } from '@/utils/stringUtils';

export const ManifestDropdown = <T extends string | number>({
  value,
  onChange,
  options,
  placeholder,
  withSearch = false,
}: {
  value: T | null;
  onChange: (value: T | null) => void;
  options: T[];
  placeholder?: string;
  withSearch?: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState<string>('');

  const filteredOptions = options.filter((option) => {
    if (!search) return true;
    return lowerCaseIncludes(option.toString(), search);
  });

  const renderOption = (option: T) => {
    return (
      <button
        type="button"
        key={option}
        className="flex w-full cursor-pointer items-center rounded-sm px-2 py-1 hover:bg-secondary"
        onClick={() => {
          onChange(option);
          setIsOpen(false);
        }}
      >
        {option}
      </button>
    );
  };

  const renderList = () => {
    const hasOptions = filteredOptions.length > 0;
    if (!hasOptions && search) {
      const searchValue = (
        typeof options[0] === 'number' ? Number(search) : search
      ) as T;
      return renderOption(searchValue);
    }
    return filteredOptions.map((option) => renderOption(option));
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger>
        <div className="flex items-center gap-2 rounded-md border border-secondary p-1.5 px-2.5 transition hover:bg-secondary-hover active:bg-secondary">
          <span className="flex min-w-0 flex-grow items-center gap-2 overflow-hidden text-ellipsis text-left">
            {value || placeholder || 'Select...'}
          </span>
          <ChevronDownIcon className="mr-0.5 h-5 w-5 transition-all" />
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="max-h-[400px] w-[494px] overflow-y-auto">
        {withSearch && (
          <Input
            placeholder="Search..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
            }}
            sx={{
              mb: 1,
            }}
          />
        )}
        <div className="flex flex-col gap-1">{renderList()}</div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
